/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PrestasiBintangGroupedChart from "layouts/dashboards/analytics/components/PrestasiBintangGroupedChart";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import { useState, useEffect, useMemo } from "react";
import { API_ESA_OFFICE, API_TIMEOUT } from "utils";
import { getDatesWithoutWeekends, hitungtotaljamkerja } from "utils/func";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { logoutUser } from "actions/AuthAction";

// Images
import eoCover from "assets/images/eo_cover.png";
import eoCover2 from "assets/images/eocover2.png";
import eoCover3 from "assets/images/eocover3.png";
import eoCover4 from "assets/images/eocover4.png";
import eo_mini1 from "assets/images/eo_mini1.png";
import eo_mini4 from "assets/images/eo_mini4.png";

// Konfigurasi axios global untuk mengirim credentials
axios.defaults.withCredentials = true;

/** Sama seperti halaman rekap-dinas: bintang dari Total Kinerja (%) */
function hitungBintangPrestasi(pct) {
  if (pct > 95) return 5;
  if (pct >= 85) return 4;
  if (pct >= 70) return 3;
  if (pct >= 55) return 2;
  return 1;
}

/** Perhitungan dan urutan sama dengan RekapLaporanDinas (totalKinerja) */
function buildPrestasiRankingFromRekap({
  dataRekapDinas,
  kinerjaData,
  apelRekapData,
  tanggalAwal,
  tanggalAkhir,
  listLibur,
}) {
  if (!dataRekapDinas?.data_pegawai?.length) return [];
  const dates = getDatesWithoutWeekends(
    tanggalAwal,
    tanggalAkhir,
    Array.isArray(listLibur) ? listLibur : []
  );
  const totalHariKerja = dates.length;
  const totalWorkingHoursIdeal = hitungtotaljamkerja(dates) || "0:00";
  const [th, tm] = String(totalWorkingHoursIdeal).split(":").map(Number);
  const totalWorkingMinutes =
    totalWorkingHoursIdeal && totalWorkingHoursIdeal !== "0:00"
      ? (th || 0) * 60 + (tm || 0)
      : 0;

  const calculatePersentaseJamKerja = (workingHours) => {
    if (!workingHours || workingHours === "0:00" || totalWorkingMinutes === 0)
      return 0;
    const [h, m] = String(workingHours).split(":").map(Number);
    const minutes = (h || 0) * 60 + (m || 0);
    return Math.round((minutes / totalWorkingMinutes) * 10000) / 100;
  };

  const calculateCorrectPercentage = (hadir, totalHari) => {
    if (!totalHari || totalHari === 0) return 0;
    return ((hadir || 0) / totalHari) * 100;
  };

  const rows = dataRekapDinas.data_pegawai.map((pegawai) => {
    const kinerja = Array.isArray(kinerjaData)
      ? kinerjaData.find(
          (k) =>
            String(k.uid) === String(pegawai.uid_RSS) ||
            (k.nama &&
              pegawai.nama_pegawai &&
              k.nama.trim() === pegawai.nama_pegawai.trim())
        )
      : null;
    const apelPegawai = apelRekapData?.data_pegawai
      ? apelRekapData.data_pegawai.find(
          (a) =>
            String(a.uid_RSS) === String(pegawai.uid_RSS) ||
            (a.nama_pegawai &&
              pegawai.nama_pegawai &&
              a.nama_pegawai.trim() === pegawai.nama_pegawai.trim())
        )
      : null;
    const workingHours = kinerja?.workingHours || "0:00";
    const hadirDaftarHadir = kinerja?.hadir ?? 0;
    const apel_pagi = apelPegawai?.apel_pagi;
    const disposisiArray = Array.isArray(pegawai.disposisi)
      ? pegawai.disposisi
      : [];
    const instruksiArray = Array.isArray(pegawai.instruksi)
      ? pegawai.instruksi
      : [];
    const tamuArray = Array.isArray(pegawai.tamu) ? pegawai.tamu : [];
    const eventsArray = Array.isArray(pegawai.events) ? pegawai.events : [];
    const laporanArray = Array.isArray(pegawai.laporan_harian)
      ? pegawai.laporan_harian
      : [];
    const totalActivities =
      instruksiArray.length +
      disposisiArray.length +
      eventsArray.length +
      tamuArray.length +
      laporanArray.length;

    const capaianJam = calculatePersentaseJamKerja(workingHours);
    const persenKehadiran = totalHariKerja
      ? calculateCorrectPercentage(hadirDaftarHadir, totalHariKerja)
      : 0;
    const persenApelPagi = totalHariKerja
      ? calculateCorrectPercentage(apel_pagi?.hadir || 0, totalHariKerja)
      : 0;
    const avgPresensiApel =
      capaianJam * 0.4 + persenKehadiran * 0.4 + persenApelPagi * 0.2;
    const skorAktivitas = Math.min(100, totalActivities * 10);
    const totalKinerja = Math.round(0.6 * avgPresensiApel + 0.4 * skorAktivitas);

    return {
      uid_RSS: pegawai.uid_RSS,
      nama_pegawai: pegawai.nama_pegawai,
      nama_jabatan: pegawai.nama_jabatan || "",
      totalKinerja,
    };
  });

  return rows
    .sort((a, b) => (b.totalKinerja ?? 0) - (a.totalKinerja ?? 0))
    .map((r, i) => ({
      ...r,
      rank: i + 1,
      bintang: hitungBintangPrestasi(r.totalKinerja ?? 0),
    }));
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Tanggal & bagian kalender WITA (konsisten dengan getTodayWITA) */
function getWitaNow() {
  const today = new Date();
  const witaOffset = 8 * 60;
  const utc = today.getTime() + today.getTimezoneOffset() * 60000;
  return new Date(utc + witaOffset * 60000);
}

function witaTodayISO() {
  return getWitaNow().toISOString().split("T")[0];
}

/** yyyy-mm dari Date */
function toYearMonth(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

/** Periode bulan terpilih: jika bulan berjalan → tgl 1 s/d hari ini WITA; jika bulan lalu → tgl 1 s/d akhir bulan */
function rangeBulanTerpilih(yearMonth) {
  const [y, m] = yearMonth.split("-").map(Number);
  const w = getWitaNow();
  const isBulanBerjalan = y === w.getFullYear() && m - 1 === w.getMonth();
  const start = `${y}-${pad2(m)}-01`;
  let end;
  if (isBulanBerjalan) {
    end = witaTodayISO();
  } else {
    const lastDay = new Date(y, m, 0).getDate();
    end = `${y}-${pad2(m)}-${pad2(lastDay)}`;
  }
  return { start, end, isBulanBerjalan };
}

/** N bulan ke belakang dari awal bulan terpilih (selalu periode penuh tgl 1–akhir) */
function rangeBulanOffsetDariTerpilih(yearMonth, offsetBulan) {
  const [y, m] = yearMonth.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  first.setMonth(first.getMonth() - offsetBulan);
  const py = first.getFullYear();
  const pm = first.getMonth() + 1;
  const lastDay = new Date(py, pm, 0).getDate();
  return {
    start: `${py}-${pad2(pm)}-01`,
    end: `${py}-${pad2(pm)}-${pad2(lastDay)}`,
  };
}

function hitungDistribusiBintang(ranking) {
  const c = [0, 0, 0, 0, 0];
  (ranking || []).forEach((r) => {
    const b = r.bintang;
    if (b >= 1 && b <= 5) c[b - 1] += 1;
  });
  return c;
}

/** Sumbu X: hanya simbol bintang (jumlah menunjukkan tingkat 1–5) */
const LABEL_SUMBU_X_BINTANG = Array.from({ length: 5 }, (_, i) => "\u2605".repeat(i + 1));

function Analytics() {
  const [dinasId, setDinasId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    overview: {
      totalPegawai: 0,
      totalDinas: 0,
      totalAplikasi: 0,
      totalAset: 0,
    },
    kehadiran: {
      persentasePagi: 0,
      persentaseSiang: 0,
      totalHadir: 0,
      totalPegawai: 0,
      detailStatus: {
        hadir: 0,
        izin: 0,
        sakit: 0,
        dinasLuar: 0,
        tanpaBerita: 0,
      },
    },
    produktivitas: {
      totalLaporan: 0,
      totalKegiatan: 0,
      totalOutput: 0,
      dinasStats: [],
    },
    dokumen: {
      totalDisposisi: 0,
      totalSurat: 0,
      totalInstruksi: 0,
      totalAset: 0,
    },
  });
  const [isMobile, setIsMobile] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [prestasiRanking, setPrestasiRanking] = useState([]);
  /** { value: 'yyyy-mm', label } */
  const [bulanTerpilih, setBulanTerpilih] = useState(null);
  const [grafikBintangSeries, setGrafikBintangSeries] = useState([
    { label: "Bulan terpilih", data: [0, 0, 0, 0, 0] },
    { label: "1 bulan sebelumnya", data: [0, 0, 0, 0, 0] },
    { label: "2 bulan sebelumnya", data: [0, 0, 0, 0, 0] },
  ]);
  const [grafikFooterLines, setGrafikFooterLines] = useState([]);
  const [selectedRangeForCopy, setSelectedRangeForCopy] = useState({ start: "", end: "" });
  const navigate = useNavigate();

  const opsiBulan = useMemo(() => {
    const w = getWitaNow();
    const opts = [];
    for (let i = 0; i < 24; i++) {
      const d = new Date(w.getFullYear(), w.getMonth() - i, 1);
      const value = toYearMonth(d);
      const label = d.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
      opts.push({ value, label });
    }
    return opts;
  }, []);

  useEffect(() => {
    if (opsiBulan.length && !bulanTerpilih) {
      setBulanTerpilih(opsiBulan[0]);
    }
  }, [opsiBulan, bulanTerpilih]);

  // Array gambar untuk slider - desktop dan mobile
  const coverImagesDesktop = [eoCover, eoCover2, eoCover3, eoCover4];
  const coverImagesMobile = [eo_mini1, eo_mini4];
  const coverImages = isMobile ? coverImagesMobile : coverImagesDesktop;

  // Deteksi ukuran layar untuk mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 960); // md breakpoint biasanya 960px
      // Reset index ketika switch antara mobile dan desktop
      setCurrentImageIndex(0);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auto-slide banner
  useEffect(() => {
    const imageCount = coverImages.length;
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % imageCount
      );
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [isMobile]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
    } else {
      logoutUser(navigate);
    }
  }, []);

  const formatPeriode = (tAwal, tAkhir) =>
    `${new Date(tAwal).toLocaleDateString("id-ID")} – ${new Date(tAkhir).toLocaleDateString("id-ID")}`;

  const fetchRankingPeriode = async (tAwal, tAkhir, unitKerja, listLibur) => {
    const smartDhKinerjaPromise = unitKerja
      ? axios.post(
          "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/instansi/reguler/update",
          {
            tAwal,
            tAkhir,
            unitKerja,
          },
          {
            headers: { "Content-Type": "application/json" },
            timeout: API_TIMEOUT,
          }
        )
      : Promise.resolve({ data: { success: 0, data: [] } });

    const [rekapDinasRes, apelRekapRes, smartDhKinerjaRes] = await Promise.all([
      axios.get(
        `${API_ESA_OFFICE}laporan-harian/dinas/${dinasId}/rekap?tanggal_mulai=${tAwal}&tanggal_akhir=${tAkhir}`,
        { timeout: API_TIMEOUT }
      ),
      axios.get(
        `${API_ESA_OFFICE}apel-harian/dinas/${dinasId}/rekap?tanggal_mulai=${tAwal}&tanggal_akhir=${tAkhir}`,
        { timeout: API_TIMEOUT }
      ),
      smartDhKinerjaPromise,
    ]);

    const dataRekapDinas = rekapDinasRes?.data || null;
    const apelRekapPayload = apelRekapRes?.data?.data_pegawai
      ? apelRekapRes.data
      : null;
    const kinerjaData =
      smartDhKinerjaRes?.data?.success === 1 && Array.isArray(smartDhKinerjaRes.data.data)
        ? smartDhKinerjaRes.data.data
        : [];

    return buildPrestasiRankingFromRekap({
      dataRekapDinas,
      kinerjaData,
      apelRekapData: apelRekapPayload,
      tanggalAwal: tAwal,
      tanggalAkhir: tAkhir,
      listLibur,
    });
  };

  const fetchDashboardData = async () => {
    if (!dinasId || !bulanTerpilih?.value) return;
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));
      const unitKerja = user?.nama_dinas;
      const ym = bulanTerpilih.value;
      const cur = rangeBulanTerpilih(ym);
      const m1 = rangeBulanOffsetDariTerpilih(ym, 1);
      const m2 = rangeBulanOffsetDariTerpilih(ym, 2);

      const liburRes = await axios.get(
        "https://smartservice.rotendaokab.go.id/api/admin/libur",
        { timeout: API_TIMEOUT }
      );
      const listLibur =
        liburRes?.status === 200 && liburRes.data?.data ? liburRes.data.data : [];

      const [rankingCur, rankingM1, rankingM2] = await Promise.all([
        fetchRankingPeriode(cur.start, cur.end, unitKerja, listLibur),
        fetchRankingPeriode(m1.start, m1.end, unitKerja, listLibur),
        fetchRankingPeriode(m2.start, m2.end, unitKerja, listLibur),
      ]);

      const labelM1 = new Date(m1.start).toLocaleDateString("id-ID", {
        month: "long",
        year: "numeric",
      });
      const labelM2 = new Date(m2.start).toLocaleDateString("id-ID", {
        month: "long",
        year: "numeric",
      });

      setGrafikBintangSeries([
        {
          label: bulanTerpilih.label,
          data: hitungDistribusiBintang(rankingCur),
        },
        {
          label: labelM1,
          data: hitungDistribusiBintang(rankingM1),
        },
        {
          label: labelM2,
          data: hitungDistribusiBintang(rankingM2),
        },
      ]);
      setGrafikFooterLines([
        `Bulan terpilih: ${bulanTerpilih.label}`,
        `1 bulan ke belakang: ${labelM1}`,
        `2 bulan ke belakang: ${labelM2}`,
      ]);
      setPrestasiRanking(rankingCur);
      setSelectedRangeForCopy({ start: cur.start, end: cur.end });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dinasId && bulanTerpilih?.value) {
      fetchDashboardData();
    }
  }, [dinasId, bulanTerpilih?.value]);

  return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pb={3}>
          {/* Banner Header Slider */}
          <MDBox
            mb={3}
            sx={{
              position: "relative",
              width: "100%",
              height: "200px",
              overflow: "hidden",
              borderRadius: "xl",
            }}
          >
            {coverImages.map((image, index) => (
              <MDBox
                key={index}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: index === currentImageIndex ? 1 : 0,
                  transition: "opacity 1s ease-in-out",
                }}
              >
                <img
                  src={image}
                  alt={`ESA Office Banner ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </MDBox>
            ))}
          </MDBox>
        <MDBox mb={3} ml={1}>
          <MDTypography variant="h4" fontWeight="bold">
            Dashboard ESA Office
          </MDTypography>
          <MDTypography
            variant="button"
            fontWeight="regular"
            sx={{ fontSize: "16px", color: "#737373" }}
          >
            Monitor keseluruhan aktivitas organisasi - kehadiran, produktivitas,
            dan manajemen dokumen
          </MDTypography>
        </MDBox>
        <MDBox>
          <Card sx={{ mb: 3, p: 2 }}>
            <MDBox
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "flex-start" }}
              gap={2}
            >
              <MDBox flexShrink={0} width={{ xs: "100%", md: 340 }}>
                <MDTypography variant="subtitle2" color="text" mb={1}>
                  Periode data (peringkat & grafik)
                </MDTypography>
                <Autocomplete
                  options={opsiBulan}
                  value={bulanTerpilih}
                  onChange={(e, v) => v && setBulanTerpilih(v)}
                  getOptionLabel={(o) => o?.label || ""}
                  isOptionEqualToValue={(a, b) => a?.value === b?.value}
                  renderInput={(params) => (
                    <TextField {...params} label="Bulan" placeholder="Pilih bulan" size="small" />
                  )}
                  fullWidth
                />
                <MDTypography variant="caption" color="text.secondary" display="block" mt={1}>
                  Jika bulan terpilih adalah bulan berjalan (WITA), data dihitung tanggal 1–hari ini.
                  Bulan lain: tanggal 1–akhir bulan. Dua seri perbandingan memakai bulan penuh.
                </MDTypography>
              </MDBox>
              <MDBox
                flex={1}
                sx={{
                  borderLeft: { md: "1px solid" },
                  borderTop: { xs: "1px solid", md: "none" },
                  borderColor: "divider",
                  pl: { md: 2 },
                  pt: { xs: 2, md: 0 },
                }}
              >
                <MDTypography variant="subtitle2" color="text" mb={1}>
                  Total Kinerja (%) → ikon bintang
                </MDTypography>
                <MDBox
                  display="flex"
                  flexWrap="wrap"
                  alignItems="center"
                  columnGap={2}
                  rowGap={1.25}
                >
                  {[
                    { stars: 5, range: "> 95%" },
                    { stars: 4, range: "85% – 95%" },
                    { stars: 3, range: "70% – < 85%" },
                    { stars: 2, range: "55% – < 70%" },
                    { stars: 1, range: "< 55%" },
                  ].map(({ stars, range }) => (
                    <MDBox
                      key={stars}
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{ flexWrap: "nowrap" }}
                    >
                      <MDBox display="flex" alignItems="center" component="span" sx={{ lineHeight: 0 }}>
                        {Array.from({ length: stars }).map((_, i) => (
                          <Icon key={i} sx={{ fontSize: 20, color: "#F9A825" }}>
                            star
                          </Icon>
                        ))}
                      </MDBox>
                      <MDTypography variant="caption" color="text.secondary" component="span">
                        {range}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
          <PrestasiBintangGroupedChart
            title="Distribusi bintang prestasi — perbandingan tiga bulan"
            labels={LABEL_SUMBU_X_BINTANG}
            series={grafikBintangSeries}
            footerLines={grafikFooterLines.length ? grafikFooterLines : ["—"]}
          />
        </MDBox>
        {/* Peringkat prestasi — data & rumus sama dengan Rekap Laporan Dinas */}
        <MDBox mt={3} px={1}>
          <MDTypography variant="h6" color="text" mb={0.5}>
            Peringkat Prestasi Pegawai
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" mb={2}>
            Berdasarkan kolom Total Kinerja di halaman Rekap Dinas (presensi, apel,
            aktivitas). Periode bulan terpilih:{" "}
            {selectedRangeForCopy.start && selectedRangeForCopy.end
              ? formatPeriode(selectedRangeForCopy.start, selectedRangeForCopy.end)
              : "—"}
            . Nilai tertinggi di atas.
          </MDTypography>
          <Grid container spacing={2}>
            {prestasiRanking.map((p) => (
              <Grid item xs={12} key={p.uid_RSS}>
                <Card sx={{ width: "100%" }}>
                  <MDBox
                    p={2}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    flexWrap="wrap"
                  >
                    <MDBox position="relative" flexShrink={0}>
                      <img
                        src={`https://smartdh.rotendaokab.go.id/uploads/${p.uid_RSS}.jpg`}
                        alt={p.nama_pegawai}
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "2px solid #e0e0e0",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <MDBox
                        display="none"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          backgroundColor: "grey.200",
                          color: "grey.500",
                        }}
                      >
                        <Icon sx={{ fontSize: 28 }}>person</Icon>
                      </MDBox>
                      <MDBox
                        position="absolute"
                        top={-6}
                        left={-6}
                        sx={{
                          backgroundColor:
                            p.rank === 1
                              ? "#FFD700"
                              : p.rank === 2
                              ? "#C0C0C0"
                              : p.rank === 3
                              ? "#CD7F32"
                              : "#1976d2",
                          color: p.rank > 3 ? "#fff" : "#000",
                          borderRadius: "50%",
                          width: 26,
                          height: 26,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 11,
                          border: "2px solid #fff",
                        }}
                      >
                        {p.rank}
                      </MDBox>
                    </MDBox>
                    <MDBox flex={1} minWidth={0}>
                      <MDTypography variant="button" fontWeight="bold" display="block">
                        {p.nama_pegawai}
                      </MDTypography>
                      {p.nama_jabatan ? (
                        <MDTypography variant="caption" color="text.secondary">
                          {p.nama_jabatan}
                        </MDTypography>
                      ) : null}
                    </MDBox>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                      justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                    >
                      <MDBox display="flex" alignItems="center" gap={0.25}>
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Icon
                            key={i}
                            sx={{
                              fontSize: 22,
                              color: i <= p.bintang ? "#F9A825" : "#e0e0e0",
                            }}
                          >
                            star
                          </Icon>
                        ))}
                      </MDBox>
                      <MDTypography variant="h6" fontWeight="bold" sx={{ color: "primary.main" }}>
                        {p.totalKinerja}%
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            ))}
            {prestasiRanking.length === 0 && !loading && (
              <Grid item xs={12}>
                <Card>
                  <MDBox textAlign="center" py={4}>
                    <Icon sx={{ fontSize: 48, color: "text.secondary" }}>
                      emoji_events
                    </Icon>
                    <MDTypography variant="button" color="text" display="block" mt={1}>
                      Belum ada data peringkat untuk periode ini
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            )}
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Analytics;
